import { useHistory, useParams } from "react-router-dom";
import { type CommitmentManagersModel } from "@doitintl/cmp-models";
import { type ModelReference } from "@doitintl/models-any";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton, MenuItem, Select, type SelectChangeEvent, Stack, Typography } from "@mui/material";

import useSegmentTrackEvent from "../../utils/useSegmentTrackEvent";
import { CommitmentActions } from "./CommitmentActions";

interface CommitmentManagerHeaderProps {
  docRef: ModelReference<CommitmentManagersModel>;
  name?: string;
  fromYear?: number;
  toYear?: number;
  periods?: CommitmentManagersModel["periods"];
  selectedPeriod: number;
  onPeriodChange: (newValue: number) => void;
}

export const CommitmentManagerHeader = ({
  name,
  docRef,
  periods = [],
  selectedPeriod,
  onPeriodChange,
}: CommitmentManagerHeaderProps) => {
  const history = useHistory();
  const { commitmentId } = useParams<{ commitmentId: string }>();
  const { trackEvent } = useSegmentTrackEvent();

  const handlePeriodChange = (event: SelectChangeEvent<number>) => {
    trackEvent("Switch Commitment Period", {
      feature: "Commitment Manager",
      source: "Commitment Manager",
      periodIndex: event.target.value,
    });
    onPeriodChange(event.target.value as number);
  };

  return (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        sx={{
          alignItems: "flex-start",
          mb: 2,
          mt: 2,
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Stack direction="row" alignItems="center">
          <IconButton
            aria-label="Back"
            onClick={() => {
              history.goBack();
            }}
            size="small"
          >
            <ArrowBackIcon color="primary" />
          </IconButton>
          <Typography
            variant="h1"
            sx={{
              ml: 1,
              mr: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </Typography>
          <CommitmentActions commitmentId={commitmentId} docRef={docRef} onDelete={history.goBack} />
        </Stack>

        {periods.length > 1 && (
          <Select value={selectedPeriod} onChange={handlePeriodChange} sx={{ minWidth: 300 }}>
            {periods.map((period, index) => (
              <MenuItem key={index} value={index}>
                {`Period ${index + 1} (${period.startDate.toDate().toLocaleDateString(undefined, {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })} - ${period.endDate.toDate().toLocaleDateString(undefined, {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })})`}
              </MenuItem>
            ))}
          </Select>
        )}
      </Stack>
    </>
  );
};
